/**
 * localDB.js — Unified local storage abstraction
 *
 * Electron  → SQLite via IPC (better-sqlite3 in main process)
 * Browser   → IndexedDB
 *
 * Stores:
 *   offline_orders    — orders buffered locally
 *   offline_kds_events— KDS status-transition events
 *   offline_meta      — key/value metadata (lastSync, etc.)
 */

const DB_NAME = 'RestaurantPOSOffline';
// v2: adds the order_mirror store (whole-business-day order blueprint).
const DB_VERSION = 2;

const isElectron = () =>
  typeof window !== 'undefined' && window.electron?.isElectron === true;

// ─── IndexedDB helpers ────────────────────────────────────────────────────────

let _idb = null;

function openIDB() {
  if (_idb) return Promise.resolve(_idb);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = e.target.result;

      if (!db.objectStoreNames.contains('offline_orders')) {
        const os = db.createObjectStore('offline_orders', { keyPath: 'localId' });
        os.createIndex('branchId', 'branchId', { unique: false });
        os.createIndex('status', 'status', { unique: false });
      }

      if (!db.objectStoreNames.contains('offline_kds_events')) {
        const ks = db.createObjectStore('offline_kds_events', { keyPath: 'eventId' });
        ks.createIndex('branchId', 'branchId', { unique: false });
        ks.createIndex('orderId', 'orderId', { unique: false });
      }

      if (!db.objectStoreNames.contains('offline_meta')) {
        db.createObjectStore('offline_meta', { keyPath: 'key' });
      }

      if (!db.objectStoreNames.contains('order_mirror')) {
        const ms = db.createObjectStore('order_mirror', { keyPath: 'order_key' });
        ms.createIndex('branchId', 'branch_id', { unique: false });
        ms.createIndex('branchDay', ['branch_id', 'business_date'], { unique: false });
        ms.createIndex('localId', 'local_id', { unique: false });
      }
    };

    req.onsuccess = () => { _idb = req.result; resolve(_idb); };
    req.onerror  = () => reject(req.error);
  });
}

function idbPut(storeName, data) {
  return openIDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx  = db.transaction(storeName, 'readwrite');
        const req = tx.objectStore(storeName).put(data);
        req.onsuccess = () => resolve(req.result);
        tx.onerror    = () => reject(tx.error);
      })
  );
}

function idbGet(storeName, key) {
  return openIDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx  = db.transaction(storeName, 'readonly');
        const req = tx.objectStore(storeName).get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror   = () => reject(req.error);
      })
  );
}

function idbGetAllByIndex(storeName, indexName, value) {
  return openIDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx  = db.transaction(storeName, 'readonly');
        const req = tx.objectStore(storeName).index(indexName).getAll(IDBKeyRange.only(value));
        req.onsuccess = () => resolve(req.result ?? []);
        req.onerror   = () => reject(req.error);
      })
  );
}

function idbDelete(storeName, key) {
  return openIDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx  = db.transaction(storeName, 'readwrite');
        const req = tx.objectStore(storeName).delete(key);
        req.onsuccess = () => resolve();
        tx.onerror    = () => reject(tx.error);
      })
  );
}

/** Delete all records in storeName where index indexName === value */
function idbClearByIndex(storeName, indexName, value) {
  return openIDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx    = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const req   = store.index(indexName).openCursor(IDBKeyRange.only(value));

        req.onsuccess = (e) => {
          const cursor = e.target.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          }
        };

        tx.oncomplete = () => resolve();
        tx.onerror    = () => reject(tx.error);
      })
  );
}

// ─── Electron IPC helpers ─────────────────────────────────────────────────────

const ipc = {
  saveOrder:        (order)    => window.electron.dbSaveOrder(order),
  getOrder:         (localId)  => window.electron.dbGetOrder(localId),
  getAllOrders:      (branchId) => window.electron.dbGetAllOrders(branchId),
  deleteOrder:      (localId)  => window.electron.dbDeleteOrder(localId),
  deleteOrderIfUnchanged: (localId, updatedAt) => {
    if (!window.electron.dbDeleteOrderIfUnchanged) throw new Error('conditional-delete IPC unavailable');
    return window.electron.dbDeleteOrderIfUnchanged(localId, updatedAt);
  },
  clearOrders:      (branchId) => window.electron.dbClearOrders(branchId),
  saveKDSEvent:     (event)    => window.electron.dbSaveKDSEvent(event),
  getAllKDSEvents:   (branchId) => window.electron.dbGetAllKDSEvents(branchId),
  clearKDSEvents:   (branchId) => window.electron.dbClearKDSEvents(branchId),
  getMeta:          (key)      => window.electron.dbGetMeta(key),
  setMeta:          (key, val) => window.electron.dbSetMeta(key, val),
  cleanup:          (branchId) => window.electron.dbCleanup?.(branchId),
  // Mirror IPC — THROW when the preload lacks the method (older installed
  // build) so electronWithFallback falls through to IndexedDB. `?.` here
  // would return undefined, which reads as SUCCESS and silently drops the
  // write (zaidm test 2026-07-27: empty mirror → occupied tables vanished).
  mirrorUpsertMany: (rows) => {
    if (!window.electron.mirrorUpsertMany) throw new Error('mirror IPC unavailable');
    return window.electron.mirrorUpsertMany(rows);
  },
  mirrorGetActive: (branchId) => {
    if (!window.electron.mirrorGetActive) throw new Error('mirror IPC unavailable');
    return window.electron.mirrorGetActive(branchId);
  },
  mirrorGetDay: (branchId, day) => {
    if (!window.electron.mirrorGetDay) throw new Error('mirror IPC unavailable');
    return window.electron.mirrorGetDay(branchId, day);
  },
  mirrorCleanupDays: (branchId, keepDay) => {
    if (!window.electron.mirrorCleanupDays) throw new Error('mirror IPC unavailable');
    return window.electron.mirrorCleanupDays(branchId, keepDay);
  },
};

// ─── IndexedDB implementations for the order mirror (browser + fallback) ─────

const MIRROR_ACTIVE_STATUSES = ['confirmed', 'preparing', 'ready', 'served', 'billed'];

async function idbMirrorUpsertMany(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return { success: true, upserted: 0 };
  const db = await openIDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction('order_mirror', 'readwrite');
    const store = tx.objectStore('order_mirror');
    for (const r of rows) {
      if (!r || !r.order_key) continue;
      // Identity migration: drop the local_id-keyed row once server-keyed.
      if (r.local_id && r.local_id !== r.order_key) store.delete(r.local_id);
      store.put(r);
    }
    tx.oncomplete = () => resolve({ success: true, upserted: rows.length });
    tx.onerror    = () => reject(tx.error);
  });
}

async function idbMirrorGetByBranch(branchId) {
  const db = await openIDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction('order_mirror', 'readonly');
    const req = tx.objectStore('order_mirror').index('branchId').getAll(IDBKeyRange.only(branchId));
    req.onsuccess = () => resolve(req.result || []);
    req.onerror   = () => reject(req.error);
  });
}

const parseMirrorData = (r) => {
  try { return typeof r.data === 'string' ? JSON.parse(r.data) : r.data; }
  catch { return null; }
};

async function idbMirrorGetActive(branchId) {
  const rows = await idbMirrorGetByBranch(branchId);
  return rows
    .filter((r) => MIRROR_ACTIVE_STATUSES.includes(String(r.status || '').toLowerCase()))
    .map(parseMirrorData)
    .filter(Boolean);
}

async function idbMirrorGetDay(branchId, businessDate) {
  const rows = await idbMirrorGetByBranch(branchId);
  return rows
    .filter((r) => r.business_date === businessDate)
    .map(parseMirrorData)
    .filter(Boolean);
}

async function idbMirrorCleanupDays(branchId, keepBusinessDate) {
  const rows = await idbMirrorGetByBranch(branchId);
  // Same rule as the SQLite handler: cancelled rows are sweepable even when
  // unsynced (nothing left to upload for a never-synced deleted order).
  const toDelete = rows.filter(
    (r) => r.business_date && r.business_date < keepBusinessDate &&
      (r.synced === 1 || String(r.status || '') === 'cancelled')
  );
  const unsyncedLeft = rows.filter(
    (r) => r.synced !== 1 && String(r.status || '') !== 'cancelled' &&
      r.business_date && r.business_date < keepBusinessDate
  ).length;
  if (toDelete.length === 0) return { success: true, deleted: 0, unsyncedLeft };
  const db = await openIDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction('order_mirror', 'readwrite');
    const store = tx.objectStore('order_mirror');
    toDelete.forEach((r) => store.delete(r.order_key));
    tx.oncomplete = () => resolve({ success: true, deleted: toDelete.length, unsyncedLeft });
    tx.onerror    = () => reject(tx.error);
  });
}

// ─── Storage failure detection ────────────────────────────────────────────────

// Main-process handlers return { sqliteUnavailable: true } when better-sqlite3
// is missing or its DB can't open — the renderer then falls back to IndexedDB
// so writes are never silently dropped.
const isSqliteUnavailable = (r) =>
  r && typeof r === 'object' && r.sqliteUnavailable === true;

let _lastBrokenReport = 0;

/**
 * Both storage backends failed — the machine has NO working local persistence.
 * Ship a tagged error to cloud logs (throttled) and raise a window event so
 * the UI can show a permanent warning banner. This must never be silent:
 * an invisible dead queue is how orders vanish.
 */
function reportStorageBroken(err, context) {
  const now = Date.now();
  if (now - _lastBrokenReport > 5 * 60 * 1000) {
    _lastBrokenReport = now;
    console.error('[storage] LOCAL STORAGE BROKEN — offline protection disabled on this PC', {
      context,
      errName: err?.name,
      errMsg: String(err?.message || err),
    });
  }
  try {
    window.dispatchEvent(new CustomEvent('pos-storage-broken', {
      detail: { context, errName: err?.name, errMsg: String(err?.message || err) },
    }));
  } catch (_) { /* non-fatal */ }
}

/** Electron: try SQLite via IPC, fall back to IndexedDB, report if both die. */
async function electronWithFallback(ipcCall, idbCall, context) {
  try {
    const r = await ipcCall();
    if (!isSqliteUnavailable(r)) return r;
  } catch (_) {
    // SQLite path threw — fall through to IndexedDB
  }
  try {
    return await idbCall();
  } catch (err) {
    reportStorageBroken(err, context);
    throw err;
  }
}

/** Browser: IndexedDB only — report loudly if it's broken. */
async function browserCall(idbCall, context) {
  try {
    return await idbCall();
  } catch (err) {
    reportStorageBroken(err, context);
    throw err;
  }
}

// ─── Unified public API ───────────────────────────────────────────────────────

const localDB = {
  /** Save / update an order locally. `order.localId` is the primary key. */
  async saveOrder(order) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.saveOrder(order),
        () => idbPut('offline_orders', order),
        'saveOrder'
      );
    }
    return browserCall(() => idbPut('offline_orders', order), 'saveOrder');
  },

  async getOrder(localId) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.getOrder(localId),
        () => idbGet('offline_orders', localId),
        'getOrder'
      );
    }
    return browserCall(() => idbGet('offline_orders', localId), 'getOrder');
  },

  async getAllOrders(branchId) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.getAllOrders(branchId),
        () => idbGetAllByIndex('offline_orders', 'branchId', branchId),
        'getAllOrders'
      );
    }
    return browserCall(
      () => idbGetAllByIndex('offline_orders', 'branchId', branchId),
      'getAllOrders'
    );
  },

  async deleteOrder(localId) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.deleteOrder(localId),
        () => idbDelete('offline_orders', localId),
        'deleteOrder'
      );
    }
    return browserCall(() => idbDelete('offline_orders', localId), 'deleteOrder');
  },

  /**
   * Delete the row ONLY if its updatedAt still equals `updatedAt` (post-sync
   * settle). Electron: a single atomic SQL statement. Fallback (web / older
   * shell): read-compare-delete — a small TOCTOU window, same as before.
   * Returns { success, deleted } where deleted is 0 when the row changed or
   * was already gone.
   */
  async deleteOrderIfUnchanged(localId, updatedAt) {
    if (isElectron()) {
      try {
        return await ipc.deleteOrderIfUnchanged(localId, updatedAt);
      } catch (_) { /* older shell without the handler — fall through */ }
    }
    const current = await this.getOrder(localId).catch(() => null);
    if (!current) return { success: true, deleted: 0, missing: true };
    if (String(current.updatedAt || '') !== String(updatedAt || '')) {
      return { success: true, deleted: 0 };
    }
    await this.deleteOrder(localId);
    return { success: true, deleted: 1 };
  },

  /** Remove every locally-stored order for this branch. */
  async clearOrders(branchId) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.clearOrders(branchId),
        () => idbClearByIndex('offline_orders', 'branchId', branchId),
        'clearOrders'
      );
    }
    return browserCall(
      () => idbClearByIndex('offline_orders', 'branchId', branchId),
      'clearOrders'
    );
  },

  // ── Order mirror (whole-business-day blueprint) ─────────────────────────────
  // The mirror is READ-ONLY for sync: uploads come exclusively from
  // offline_orders. These methods never throw — the mirror is best-effort and
  // must never break an order flow.

  async mirrorUpsertMany(rows) {
    try {
      if (isElectron()) {
        return await electronWithFallback(
          () => ipc.mirrorUpsertMany(rows),
          () => idbMirrorUpsertMany(rows),
          'mirrorUpsertMany'
        );
      }
      return await browserCall(() => idbMirrorUpsertMany(rows), 'mirrorUpsertMany');
    } catch (err) {
      console.warn('[mirror] upsert failed (non-fatal)', { count: rows?.length, errMsg: String(err?.message || err) });
      return { success: false };
    }
  },

  async mirrorGetActive(branchId) {
    try {
      if (isElectron()) {
        return await electronWithFallback(
          () => ipc.mirrorGetActive(branchId),
          () => idbMirrorGetActive(branchId),
          'mirrorGetActive'
        );
      }
      return await browserCall(() => idbMirrorGetActive(branchId), 'mirrorGetActive');
    } catch (_) {
      return [];
    }
  },

  async mirrorGetDay(branchId, businessDate) {
    try {
      if (isElectron()) {
        return await electronWithFallback(
          () => ipc.mirrorGetDay(branchId, businessDate),
          () => idbMirrorGetDay(branchId, businessDate),
          'mirrorGetDay'
        );
      }
      return await browserCall(() => idbMirrorGetDay(branchId, businessDate), 'mirrorGetDay');
    } catch (_) {
      return [];
    }
  },

  async mirrorCleanupDays(branchId, keepBusinessDate) {
    try {
      if (isElectron()) {
        return await electronWithFallback(
          () => ipc.mirrorCleanupDays(branchId, keepBusinessDate),
          () => idbMirrorCleanupDays(branchId, keepBusinessDate),
          'mirrorCleanupDays'
        );
      }
      return await browserCall(() => idbMirrorCleanupDays(branchId, keepBusinessDate), 'mirrorCleanupDays');
    } catch (_) {
      return { success: false, deleted: 0 };
    }
  },

  /**
   * Post-sync storage cleanup: purge stale draft carts (>1 day), KDS events
   * (>7 days) and reclaim SQLite file space. Electron/SQLite only — the
   * IndexedDB path has no draft/VACUUM problem worth a fallback implementation.
   * Never throws; cleanup must not fail a successful sync.
   */
  async cleanupStorage(branchId) {
    if (!isElectron()) return { skipped: 'browser' };
    try {
      // Disambiguate the two failure modes in app_logs:
      //  'ipc-missing'        → preload lacks dbCleanup (installed main/preload
      //                         OLDER than the renderer — packaging problem;
      //                         seen on 1.0.43/1.0.44 with healthy SQLite)
      //  'sqlite-unavailable' → better-sqlite3 genuinely broken in main
      if (typeof window.electron.dbCleanup !== 'function') {
        return { skipped: 'ipc-missing' };
      }
      const res = await ipc.cleanup(branchId);
      if (res === undefined || res === null) return { skipped: 'ipc-missing' };
      if (isSqliteUnavailable(res)) return { skipped: 'sqlite-unavailable' };
      return res;
    } catch (err) {
      console.warn('[storage] cleanup failed (non-fatal)', { errMsg: String(err?.message || err) });
      return { skipped: 'error' };
    }
  },

  async saveKDSEvent(event) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.saveKDSEvent(event),
        () => idbPut('offline_kds_events', event),
        'saveKDSEvent'
      );
    }
    return browserCall(() => idbPut('offline_kds_events', event), 'saveKDSEvent');
  },

  async getAllKDSEvents(branchId) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.getAllKDSEvents(branchId),
        () => idbGetAllByIndex('offline_kds_events', 'branchId', branchId),
        'getAllKDSEvents'
      );
    }
    return browserCall(
      () => idbGetAllByIndex('offline_kds_events', 'branchId', branchId),
      'getAllKDSEvents'
    );
  },

  async clearKDSEvents(branchId) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.clearKDSEvents(branchId),
        () => idbClearByIndex('offline_kds_events', 'branchId', branchId),
        'clearKDSEvents'
      );
    }
    return browserCall(
      () => idbClearByIndex('offline_kds_events', 'branchId', branchId),
      'clearKDSEvents'
    );
  },

  async getMeta(key) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.getMeta(key),
        async () => {
          const row = await idbGet('offline_meta', key);
          return row ? row.value : null;
        },
        'getMeta'
      );
    }
    return browserCall(async () => {
      const row = await idbGet('offline_meta', key);
      return row ? row.value : null;
    }, 'getMeta');
  },

  async setMeta(key, value) {
    if (isElectron()) {
      return electronWithFallback(
        () => ipc.setMeta(key, value),
        () => idbPut('offline_meta', { key, value }),
        'setMeta'
      );
    }
    return browserCall(() => idbPut('offline_meta', { key, value }), 'setMeta');
  },

  /**
   * Probe which storage backend actually works on this machine.
   * Returns { backend: 'sqlite' | 'indexeddb' | 'none', healthy: boolean }.
   * 'none' means offline protection is dead — the UI must warn the user.
   */
  async healthCheck() {
    const probeKey = '__storage_health_probe__';
    const probeVal = String(Date.now());

    if (isElectron()) {
      try {
        const w = await ipc.setMeta(probeKey, probeVal);
        if (!isSqliteUnavailable(w)) {
          const r = await ipc.getMeta(probeKey);
          if (!isSqliteUnavailable(r) && String(r) === probeVal) {
            return { backend: 'sqlite', healthy: true };
          }
        }
      } catch (_) { /* fall through to IndexedDB probe */ }
    }

    try {
      await idbPut('offline_meta', { key: probeKey, value: probeVal });
      const row = await idbGet('offline_meta', probeKey);
      if (row && row.value === probeVal) {
        return { backend: 'indexeddb', healthy: true };
      }
    } catch (err) {
      reportStorageBroken(err, 'healthCheck');
      return { backend: 'none', healthy: false };
    }

    reportStorageBroken(new Error('no working local storage backend'), 'healthCheck');
    return { backend: 'none', healthy: false };
  },

  // ─── Offline Sessions ─────────────────────────────────────────────────────────

  /**
   * Persist the current active session for a branch.
   * Pass `null` to clear it (e.g. after End Session).
   */
  async setActiveSession(branchId, session) {
    return this.setMeta(`activeSession_${branchId}`, session);
  },

  async getActiveSession(branchId) {
    return this.getMeta(`activeSession_${branchId}`);
  },

  /**
   * Queue a locally-created session for upload when internet returns.
   * The array is stored as a single meta entry per branch.
   */
  async savePendingSession(branchId, session) {
    const existing = (await this.getMeta(`pendingSessions_${branchId}`)) || [];
    const updated  = [...existing.filter(s => s.localId !== session.localId), session];
    return this.setMeta(`pendingSessions_${branchId}`, updated);
  },

  async getPendingSessions(branchId) {
    return (await this.getMeta(`pendingSessions_${branchId}`)) || [];
  },

  async clearPendingSessions(branchId) {
    return this.setMeta(`pendingSessions_${branchId}`, []);
  },

  /**
   * Update a single pending session in the queue (e.g. after End Session offline).
   */
  async updatePendingSession(branchId, session) {
    const existing = (await this.getMeta(`pendingSessions_${branchId}`)) || [];
    const updated  = existing.map(s => s.localId === session.localId ? session : s);
    return this.setMeta(`pendingSessions_${branchId}`, updated);
  },
};

export default localDB;
